import type { ThreadMessageLike } from '@assistant-ui/react';
import type { AIAgentAction, AICoachAttachment, AICoachConversationTurn, AICoachQuickReply, AIChatMessage } from '@/api/types';
import { sanitizeCoachQuickReplies } from '@/lib/coach-quick-replies';

export type AgentTurnRole = 'user' | 'assistant';

/** A tool the agent invoked, surfaced live as a progress step. */
export interface AgentStepInfo {
  tool: string;
  args: Record<string, unknown>;
}

export interface AgentTurn {
  id: string;
  role: AgentTurnRole;
  /** Markdown text. Empty while an assistant turn is still thinking. */
  content: string;
  /** Tool calls the agent made on this assistant turn. */
  actions?: AIAgentAction[];
  /** Supplement/exercise the coach recommended — rendered as catalog cards. */
  attachments?: AICoachAttachment[];
  quickReplies?: AICoachQuickReply[];
  /** Live progress steps (tools invoked) shown while/after the turn runs. */
  steps?: AgentStepInfo[];
  /** Assistant turn is awaiting a response. */
  pending?: boolean;
  /** A detached server stream may still replace this partial local answer. */
  interrupted?: boolean;
}

export interface AgentState {
  turns: AgentTurn[];
  busy: boolean;
  error: string | null;
}

export type AgentEvent =
  | { type: 'reset' }
  | { type: 'hydrate'; turns: AgentTurn[] }
  | { type: 'error'; message: string | null }
  | { type: 'push_user'; id: string; content: string }
  | { type: 'start_assistant'; id: string }
  | { type: 'retry_assistant'; id: string }
  | { type: 'append_assistant'; id: string; delta: string }
  | { type: 'reset_assistant_content'; id: string }
  | { type: 'step_assistant'; id: string; step: AgentStepInfo }
  | { type: 'set_actions'; id: string; actions: AIAgentAction[] }
  | { type: 'patch_action'; actionId: string; result: Record<string, unknown> }
  | { type: 'set_attachments'; id: string; attachments: AICoachAttachment[] }
  | { type: 'set_quick_replies'; id: string; quickReplies: AICoachQuickReply[] }
  | { type: 'finish_assistant'; id: string; content?: string; actions?: AIAgentAction[] }
  | { type: 'interrupt_assistant'; id: string }
  | { type: 'fail_assistant'; id: string; message: string };

export const initialAgentState: AgentState = {
  turns: [],
  busy: false,
  error: null,
};

export function agentReducer(state: AgentState, ev: AgentEvent): AgentState {
  switch (ev.type) {
    case 'reset':
      return { ...initialAgentState };
    case 'hydrate':
      if (state.busy) return state;
      return {
        ...state,
        error: hasSettledRetryResponse(state.turns, ev.turns) ? null : state.error,
        turns: mergeHydratedTurns(state.turns, ev.turns),
      };
    case 'error':
      return { ...state, error: ev.message };
    case 'push_user':
      return {
        ...state,
        error: null,
        turns: [
          ...state.turns.map((turn) => ({ ...turn, quickReplies: undefined })),
          { id: ev.id, role: 'user', content: ev.content },
        ],
      };
    case 'start_assistant':
      return {
        ...state,
        busy: true,
        error: null,
        turns: [...state.turns, { id: ev.id, role: 'assistant', content: '', pending: true }],
      };
    case 'retry_assistant':
      if (!findRetryableUserTurn(state.turns)) return state;
      return {
        ...state,
        busy: true,
        error: null,
        turns: [
          ...turnsForRetry(state.turns),
          { id: ev.id, role: 'assistant', content: '', pending: true },
        ],
      };
    case 'append_assistant':
      return {
        ...state,
        turns: state.turns.map((t) =>
          t.id === ev.id
            ? { ...t, content: t.content + ev.delta, pending: true }
            : t,
        ),
      };
    case 'reset_assistant_content':
      // Drop a streamed preamble (kept steps) — real answer follows the tools.
      return {
        ...state,
        turns: state.turns.map((t) =>
          t.id === ev.id ? { ...t, content: '', pending: true } : t,
        ),
      };
    case 'step_assistant':
      return {
        ...state,
        turns: state.turns.map((t) =>
          t.id === ev.id
            ? {
                ...t,
                steps: (t.steps ?? []).some((step) => step.tool === ev.step.tool)
                  ? (t.steps ?? []).map((step) => (
                      step.tool === ev.step.tool ? ev.step : step
                    ))
                  : [...(t.steps ?? []), ev.step],
              }
            : t,
        ),
      };
    case 'set_actions':
      return {
        ...state,
        turns: state.turns.map((t) =>
          t.id === ev.id ? { ...t, actions: ev.actions } : t,
        ),
      };
    case 'patch_action':
      return {
        ...state,
        turns: state.turns.map((turn) => ({
          ...turn,
          actions: turn.actions?.map((action) => (
            action.id === ev.actionId
              ? { ...action, result: { ...action.result, ...ev.result } }
              : action
          )),
        })),
      };
    case 'set_attachments':
      return {
        ...state,
        turns: state.turns.map((t) =>
          t.id === ev.id ? { ...t, attachments: ev.attachments } : t,
        ),
      };
    case 'set_quick_replies':
      return {
        ...state,
        turns: state.turns.map((t) =>
          t.id === ev.id
            ? { ...t, quickReplies: sanitizeCoachQuickReplies(ev.quickReplies) }
            : t,
        ),
      };
    case 'finish_assistant':
      return {
        ...state,
        busy: false,
        turns: state.turns.map((t) =>
          t.id === ev.id
            ? {
                ...t,
                content: ev.content !== undefined ? ev.content : t.content,
                actions: ev.actions !== undefined ? ev.actions : t.actions,
                pending: false,
                interrupted: false,
              }
            : t,
        ),
      };
    case 'interrupt_assistant':
      return {
        ...state,
        busy: false,
        turns: state.turns.map((turn) => (
          turn.id === ev.id
            ? { ...turn, pending: false, interrupted: true }
            : turn
        )),
      };
    case 'fail_assistant':
      return {
        ...state,
        busy: false,
        error: ev.message,
        // Drop the empty placeholder so the conversation stays clean.
        turns: state.turns.filter((t) => t.id !== ev.id),
      };
    default:
      return state;
  }
}

export interface StepsPartData {
  steps: AgentStepInfo[];
  pending: boolean;
}

export function convertTurnToMessage(turn: AgentTurn): ThreadMessageLike {
  if (turn.role === 'user') {
    return { id: turn.id, role: 'user', content: turn.content };
  }

  const content: Exclude<ThreadMessageLike['content'], string>[number][] = [];

  if (turn.steps && turn.steps.length > 0) {
    content.push({
      type: 'data-coachSteps',
      data: { steps: turn.steps, pending: !!turn.pending } satisfies StepsPartData,
    });
  }
  if (turn.content) {
    content.push({ type: 'text', text: turn.content });
  }
  if (turn.actions && turn.actions.length > 0) {
    content.push({ type: 'data-coachActions', data: { actions: turn.actions } });
  }
  if (turn.attachments && turn.attachments.length > 0) {
    content.push({ type: 'data-coachAttachments', data: { attachments: turn.attachments } });
  }
  if (turn.quickReplies && turn.quickReplies.length > 0) {
    content.push({ type: 'data-coachQuickReplies', data: { quickReplies: turn.quickReplies } });
  }

  return {
    id: turn.id,
    role: 'assistant',
    content: content.length > 0 ? content : [{ type: 'text', text: '' }],
  };
}

/** Map UI turns to the wire format the agent endpoint expects. */
export function toChatMessages(turns: AgentTurn[]): AIChatMessage[] {
  return turns
    .filter((t) => t.content.trim().length > 0)
    .map((t) => ({ role: t.role, content: t.content }));
}

const LEGACY_STORAGE_KEY = 'coach.agent.turns.v1';
const LEGACY_STORAGE_PREFIX = 'coach.agent.turns.v2:';
const STORAGE_PREFIX = 'coach.agent.turns.v3:';
const ACTIVE_THREAD_PREFIX = 'coach.agent.active-thread.v1:';
const MAX_PERSISTED = 30;
const memoryTurns = new Map<string, AgentTurn[]>();
const memoryActiveThreads = new Map<string, string>();

function storageKey(userId?: number, threadId?: string): string {
  return `${STORAGE_PREFIX}${userId ?? 'anonymous'}:${threadId ?? 'current'}`;
}

function activeThreadKey(userId?: number): string {
  return `${ACTIVE_THREAD_PREFIX}${userId ?? 'anonymous'}`;
}

export function loadPersistedActiveThreadId(userId?: number): string | undefined {
  const key = activeThreadKey(userId);
  try {
    const value = sessionStorage.getItem(key) ?? memoryActiveThreads.get(key);
    return value && /^\d+$/.test(value) ? value : undefined;
  } catch {
    return memoryActiveThreads.get(key);
  }
}

export function persistActiveThreadId(threadId: string | undefined, userId?: number): void {
  const key = activeThreadKey(userId);
  if (!threadId || !/^\d+$/.test(threadId)) {
    memoryActiveThreads.delete(key);
    try {
      sessionStorage.removeItem(key);
    } catch {
      return;
    }
    return;
  }
  memoryActiveThreads.set(key, threadId);
  try {
    sessionStorage.setItem(key, threadId);
  } catch {
    return;
  }
}

export function loadPersistedTurns(userId?: number, threadId?: string): AgentTurn[] {
  try {
    const key = storageKey(userId, threadId);
    const legacyUserKey = `${LEGACY_STORAGE_PREFIX}${userId ?? 'anonymous'}`;
    sessionStorage.removeItem(LEGACY_STORAGE_KEY);
    const raw = sessionStorage.getItem(key)
      ?? sessionStorage.getItem(legacyUserKey);
    if (!raw) return memoryTurns.get(key)?.map((turn) => ({ ...turn })) ?? [];
    const parsed = JSON.parse(raw) as AgentTurn[];
    if (!Array.isArray(parsed)) return [];
    const turns = parsed
      .filter((turn) => turn?.id && !(turn.role === 'assistant' && turn.pending && !turn.content.trim()))
      .map((turn) => ({
        ...turn,
        interrupted: turn.interrupted || turn.pending || undefined,
        pending: false,
        quickReplies: sanitizeCoachQuickReplies(turn.quickReplies),
      }))
      .slice(-MAX_PERSISTED);
    sessionStorage.setItem(key, JSON.stringify(turns));
    sessionStorage.removeItem(legacyUserKey);
    sessionStorage.removeItem(LEGACY_STORAGE_KEY);
    memoryTurns.set(key, turns);
    return turns;
  } catch {
    const key = storageKey(userId, threadId);
    return memoryTurns.get(key)?.map((turn) => ({ ...turn })) ?? [];
  }
}

/** Map server-side conversation turns to UI turns (server is source of truth). */
export function serverTurnsToAgentTurns(turns: AICoachConversationTurn[]): AgentTurn[] {
  const mapped = turns
    .filter((t) => (t.content ?? '').trim().length > 0)
    .map((t) => ({
      id: t.id,
      role: t.role,
      content: t.content,
      actions: t.actions && t.actions.length > 0 ? t.actions : undefined,
      attachments: t.attachments && t.attachments.length > 0 ? t.attachments : undefined,
      quickReplies: sanitizeCoachQuickReplies(t.quick_replies),
      pending: false,
    }))
    .slice(-MAX_PERSISTED);
  let lastAssistant = -1;
  mapped.forEach((turn, index) => {
    if (turn.role === 'assistant') lastAssistant = index;
  });
  return mapped.map((turn, index) => (
    index === lastAssistant ? turn : { ...turn, quickReplies: undefined }
  ));
}

export function persistTurns(turns: AgentTurn[], userId?: number, threadId?: string): void {
  const key = storageKey(userId, threadId);
  const persisted = turns.slice(-MAX_PERSISTED).map((turn) => ({ ...turn }));
  if (persisted.length === 0) memoryTurns.delete(key);
  else memoryTurns.set(key, persisted);
  try {
    if (persisted.length === 0) {
      sessionStorage.removeItem(key);
      return;
    }
    sessionStorage.setItem(key, JSON.stringify(persisted));
  } catch {
    /* quota / disabled storage — ignore */
  }
}

export function clearPersistedTurns(userId?: number, threadId?: string): void {
  const userKeyPrefix = `${STORAGE_PREFIX}${userId ?? 'anonymous'}:`;
  const key = storageKey(userId, threadId);
  if (threadId) memoryTurns.delete(key);
  else {
    for (const storedKey of memoryTurns.keys()) {
      if (storedKey.startsWith(userKeyPrefix)) memoryTurns.delete(storedKey);
    }
  }
  try {
    if (threadId) sessionStorage.removeItem(key);
    else {
      for (let index = sessionStorage.length - 1; index >= 0; index -= 1) {
        const storedKey = sessionStorage.key(index);
        if (storedKey?.startsWith(userKeyPrefix)) sessionStorage.removeItem(storedKey);
      }
    }
    sessionStorage.removeItem(`${LEGACY_STORAGE_PREFIX}${userId ?? 'anonymous'}`);
    sessionStorage.removeItem(LEGACY_STORAGE_KEY);
    if (!threadId) persistActiveThreadId(undefined, userId);
  } catch {
    return;
  }
}

function signature(turn: AgentTurn): string {
  return `${turn.role}\u0000${turn.content.trim()}`;
}

export function turnSignatureOccurrence(turns: AgentTurn[], target: AgentTurn): number {
  const targetIndex = turns.findIndex((turn) => turn.id === target.id);
  if (targetIndex < 0) return 0;
  const targetSignature = signature(target);
  return turns
    .slice(0, targetIndex + 1)
    .filter((turn) => signature(turn) === targetSignature)
    .length;
}

export function findRetryableUserTurn(turns: AgentTurn[]): AgentTurn | undefined {
  const last = turns.at(-1);
  if (last?.role === 'user') return last;
  if (last?.role === 'assistant' && last.interrupted) {
    const previous = turns.at(-2);
    if (previous?.role === 'user') return previous;
  }
  return undefined;
}

export function turnsForRetry(turns: AgentTurn[]): AgentTurn[] {
  const last = turns.at(-1);
  return last?.role === 'assistant' && last.interrupted ? turns.slice(0, -1) : turns;
}

export function hasSettledAssistantAfterUser(
  turns: AgentTurn[],
  user: AgentTurn,
  occurrence?: number,
): boolean {
  const matches = turns
    .map((turn, index) => ({ turn, index }))
    .filter(({ turn }) => turn.role === 'user' && signature(turn) === signature(user));
  const matchingUserIndex = occurrence == null
    ? matches.at(-1)?.index
    : matches[occurrence - 1]?.index;
  if (matchingUserIndex == null) return false;
  const nextUserIndex = turns.findIndex((turn, index) => (
    index > matchingUserIndex && turn.role === 'user'
  ));
  return turns
    .slice(matchingUserIndex + 1, nextUserIndex < 0 ? undefined : nextUserIndex)
    .some((turn) => (
      turn.role === 'assistant'
      && !turn.pending
      && !turn.interrupted
      && turn.content.trim().length > 0
    ));
}

function hasSettledRetryResponse(local: AgentTurn[], server: AgentTurn[]): boolean {
  const lastLocal = local.at(-1);
  const retryUser = findRetryableUserTurn(local);
  return lastLocal?.role === 'assistant'
    && !!lastLocal.interrupted
    && !!retryUser
    && hasSettledAssistantAfterUser(
      server,
      retryUser,
      turnSignatureOccurrence(local, retryUser),
    );
}

function orderedTurnMatches(local: AgentTurn[], server: AgentTurn[]): Array<[number, number]> {
  const rows = local.length + 1;
  const columns = server.length + 1;
  const lengths = Array.from({ length: rows }, () => Array<number>(columns).fill(0));
  for (let localIndex = 1; localIndex < rows; localIndex += 1) {
    for (let serverIndex = 1; serverIndex < columns; serverIndex += 1) {
      lengths[localIndex][serverIndex] = (
        signature(local[localIndex - 1]) === signature(server[serverIndex - 1])
          ? lengths[localIndex - 1][serverIndex - 1] + 1
          : Math.max(
              lengths[localIndex - 1][serverIndex],
              lengths[localIndex][serverIndex - 1],
            )
      );
    }
  }

  const matches: Array<[number, number]> = [];
  let localIndex = local.length;
  let serverIndex = server.length;
  while (localIndex > 0 && serverIndex > 0) {
    if (
      signature(local[localIndex - 1]) === signature(server[serverIndex - 1])
      && lengths[localIndex][serverIndex]
        === lengths[localIndex - 1][serverIndex - 1] + 1
    ) {
      matches.push([localIndex - 1, serverIndex - 1]);
      localIndex -= 1;
      serverIndex -= 1;
    } else if (
      lengths[localIndex - 1][serverIndex]
      > lengths[localIndex][serverIndex - 1]
    ) {
      localIndex -= 1;
    } else {
      serverIndex -= 1;
    }
  }
  return matches.reverse();
}

function mergeServerTurn(server: AgentTurn, local?: AgentTurn): AgentTurn {
  if (!local) return server;
  const localActions = new Map(
    (local.actions ?? [])
      .filter((action) => action.id)
      .map((action) => [action.id, action]),
  );
  return {
    ...server,
    actions: server.actions?.length
      ? server.actions.map((action) => {
          const localAction = action.id ? localActions.get(action.id) : undefined;
          const foodReceipts = localAction?.result.food_receipts;
          if (!foodReceipts) return action;
          return {
            ...action,
            result: { ...action.result, food_receipts: foodReceipts },
          };
        })
      : local.actions,
    attachments: server.attachments?.length ? server.attachments : local.attachments,
  };
}

function keepCurrentQuickReplies(turns: AgentTurn[]): AgentTurn[] {
  const activeIndex = turns.at(-1)?.role === 'assistant' ? turns.length - 1 : -1;
  return turns.map((turn, index) => (
    index === activeIndex || !turn.quickReplies
      ? turn
      : { ...turn, quickReplies: undefined }
  ));
}

export function mergeHydratedTurns(local: AgentTurn[], server: AgentTurn[]): AgentTurn[] {
  if (local.length === 0) return keepCurrentQuickReplies(server.slice(-MAX_PERSISTED));
  if (server.length === 0) return keepCurrentQuickReplies(local.slice(-MAX_PERSISTED));

  const lastLocal = local.at(-1);
  const previousLocal = local.at(-2);
  if (
    lastLocal?.role === 'assistant'
    && lastLocal.interrupted
    && previousLocal?.role === 'user'
  ) {
    if (hasSettledAssistantAfterUser(
      server,
      previousLocal,
      turnSignatureOccurrence(local, previousLocal),
    )) {
      return mergeHydratedTurns(local.slice(0, -1), server);
    }
  }

  const matches = orderedTurnMatches(local, server);
  const localByServerIndex = new Map(matches.map(([localIndex, serverIndex]) => (
    [serverIndex, local[localIndex]]
  )));
  const mergedServer = server.map((turn, index) => (
    mergeServerTurn(turn, localByServerIndex.get(index))
  ));
  const containsLocalHistory = matches.length === local.length;
  if (containsLocalHistory) {
    return keepCurrentQuickReplies(mergedServer.slice(-MAX_PERSISTED));
  }

  const lastMatch = matches.at(-1);
  const localSuffix = lastMatch?.[1] === server.length - 1
    ? local.slice(lastMatch[0] + 1)
    : [];
  return keepCurrentQuickReplies([
    ...mergedServer,
    ...localSuffix,
  ].slice(-MAX_PERSISTED));
}

export function uid(): string {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}
